/**
 * `tour/SpotlightTour.tsx` — the first-visit spotlight tour (#2048).
 *
 * A fixed layer dims the page except a rounded cut-out around the current
 * stop's `data-tour` anchor; a small card beside it says what the thing is
 * (title, one sentence, "Step n of N", Back / Next / Done, Skip). Six
 * stops (see `tourSteps.ts`); a stop whose anchor is missing is skipped.
 *
 * Shows by itself once per account on a lead's team home (`useTour.ts`),
 * and again from the top bar's Help menu ("Show me around"). Escape closes; focus
 * stays in the card while it is open and returns where it was after.
 *
 * The cut-out is one rounded box with a page-sized shadow in the dim colour
 * (so it moves smoothly between stops); a transparent layer under it keeps
 * the page from taking clicks while the tour is up. No external library.
 */
import {
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";

import type { WorkspaceRole } from "../../lib/activeWorkspace";
import { cn } from "../../lib/cn";
import { Button } from "../ui";
import { type ResolvedTourStop, findAnchor, resolveTourStops } from "./tourSteps";
import { isTourLeadRole, useTourAutoShow, useTourOpenRequests } from "./useTour";

/** Space between the anchor and the cut-out's edge. */
const HOLE_PAD = 6;
/** Space between the cut-out and the card. */
const CARD_GAP = 12;
/** Gutter kept clear at the viewport's edges. */
const EDGE = 16;
const CARD_MAX_W = 320;
/** Below this width the card sits centred at the bottom of the screen. */
const PHONE_MAX_W = 640;

interface Box {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface SpotlightTourProps {
  accountId: string | null | undefined;
  /** The account's role on the active team (leads and co-leads only). */
  workspaceRole: WorkspaceRole | null | undefined;
  /** Test seam: replaces the "page has settled" check of the auto-show. */
  isSettled?: (() => boolean) | undefined;
  /** Test seam: replaces the "anchor is on screen" check. */
  isShown?: ((el: Element) => boolean) | undefined;
}

export function SpotlightTour({
  accountId,
  workspaceRole: role,
  isSettled,
  isShown,
}: SpotlightTourProps) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stops, setStops] = useState<ResolvedTourStop[]>([]);
  const [index, setIndex] = useState(0);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const resolve = useCallback(() => resolveTourStops(document, isShown), [isShown]);

  const start = useCallback(() => {
    const found = resolve();
    if (found.length === 0) return;
    returnFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setStops(found);
    setIndex(0);
    setOpen(true);
  }, [resolve]);

  const close = useCallback(() => {
    setOpen(false);
    const back = returnFocusRef.current;
    returnFocusRef.current = null;
    if (back?.isConnected) back.focus();
  }, []);

  useTourAutoShow({ accountId, role, pathname: location.pathname, onShow: start, isSettled });
  // "Show me around" — let the Help menu finish closing (it hands focus
  // back to its trigger) before the card takes focus.
  useTourOpenRequests(() => {
    if (!isTourLeadRole(role)) return;
    window.setTimeout(start, 60);
  });

  // Moving between stops re-reads the anchors, so a stop whose anchor
  // arrived late (the first-roster card loads after the shell) still shows.
  const go = useCallback(
    (delta: 1 | -1) => {
      const currentId = stops[index]?.id;
      const fresh = resolve();
      const at = fresh.findIndex((s) => s.id === currentId);
      const next = (at === -1 ? index : at) + delta;
      if (next >= fresh.length) {
        close();
        return;
      }
      setStops(fresh);
      setIndex(Math.max(0, next));
    },
    [stops, index, resolve, close],
  );

  if (!open || stops.length === 0) return null;
  const stop = stops[Math.min(index, stops.length - 1)];
  if (!stop) return null;
  return createPortal(
    <TourLayer
      stop={stop}
      step={index + 1}
      total={stops.length}
      onBack={() => go(-1)}
      onNext={() => go(1)}
      onClose={close}
    />,
    document.body,
  );
}

function TourLayer({
  stop,
  step,
  total,
  onBack,
  onNext,
  onClose,
}: {
  stop: ResolvedTourStop;
  step: number;
  total: number;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const titleId = useId();
  const bodyId = useId();
  const cardRef = useRef<HTMLDivElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const [hole, setHole] = useState<Box | null>(null);
  const [viewport, setViewport] = useState(() => ({
    w: window.innerWidth,
    h: window.innerHeight,
  }));
  const [cardH, setCardH] = useState(180);
  const last = step === total;

  const target = useCallback(
    () => (stop.element.isConnected ? stop.element : findAnchor(stop.anchor)),
    [stop],
  );

  const measure = useCallback(() => {
    const el = target();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    setViewport((v) => (v.w === vw && v.h === vh ? v : { w: vw, h: vh }));
    const h = cardRef.current?.offsetHeight || 180;
    setCardH((prev) => (prev === h ? prev : h));
    if (!el) return;
    const r = el.getBoundingClientRect();
    const next = {
      top: r.top - HOLE_PAD,
      left: r.left - HOLE_PAD,
      width: r.width + HOLE_PAD * 2,
      height: r.height + HOLE_PAD * 2,
    };
    setHole((prev) => (prev && sameBox(prev, next) ? prev : next));
  }, [target]);

  // Bring the stop on screen, then measure.
  useLayoutEffect(() => {
    const el = target();
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ block: "center", inline: "nearest" });
      }
    }
    measure();
  }, [target, measure]);

  // Follow the page while the tour is up: resizes, scrolls in any
  // scroller, and the page re-laying itself out as its data lands. One
  // read per frame; state only changes when a box actually moves.
  useEffect(() => {
    let raf = 0;
    const loop = () => {
      measure();
      raf = window.requestAnimationFrame(loop);
    };
    raf = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(raf);
  }, [measure]);

  // Focus the primary button on every stop.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-focus when the stop changes
  useEffect(() => {
    primaryRef.current?.focus();
  }, [stop.id]);

  // Escape closes, wherever focus is.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  // Basic focus trap: Tab cycles within the card.
  const onCardKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab" || !cardRef.current) return;
    const items = Array.from(
      cardRef.current.querySelectorAll<HTMLElement>("button:not([disabled])"),
    );
    if (items.length === 0) return;
    const first = items[0];
    const lastItem = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || !cardRef.current.contains(active))) {
      e.preventDefault();
      lastItem?.focus();
    } else if (!e.shiftKey && (active === lastItem || !cardRef.current.contains(active))) {
      e.preventDefault();
      first?.focus();
    }
  };

  const phone = viewport.w < PHONE_MAX_W;
  const cardW = Math.min(CARD_MAX_W, viewport.w - EDGE * 2);
  let cardStyle: CSSProperties;
  if (phone || !hole) {
    cardStyle = { left: EDGE, right: EDGE, bottom: EDGE, marginInline: "auto" };
  } else {
    const below = hole.top + hole.height + CARD_GAP;
    const above = hole.top - CARD_GAP - cardH;
    const top =
      below + cardH <= viewport.h - EDGE
        ? below
        : above >= EDGE
          ? above
          : Math.max(EDGE, viewport.h - cardH - EDGE);
    const left = clamp(hole.left + hole.width / 2 - cardW / 2, EDGE, viewport.w - cardW - EDGE);
    cardStyle = { top, left };
  }

  return (
    <div data-testid="spotlight-tour" data-step={stop.id}>
      {/* Keeps the page from taking clicks while the tour is up. */}
      <div className="fixed inset-0 z-[60]" aria-hidden="true" />
      {hole ? (
        <div
          aria-hidden="true"
          data-testid="spotlight-tour-hole"
          className={cn(
            "pointer-events-none fixed z-[60] rounded-lg",
            "transition-[top,left,width,height] duration-200 ease-out motion-reduce:transition-none",
          )}
          style={{
            top: hole.top,
            left: hole.left,
            width: hole.width,
            height: hole.height,
            // The accent ring, then the dim layer as one page-sized shadow.
            boxShadow: `0 0 0 2px rgb(var(--color-accent)), 0 0 0 200vmax ${dimColour()}`,
          }}
        />
      ) : (
        <div
          aria-hidden="true"
          className="fixed inset-0 z-[60]"
          style={{ background: dimColour() }}
        />
      )}
      <div
        ref={cardRef}
        // biome-ignore lint/a11y/useSemanticElements: a positioned card, not a native <dialog> (its top-layer modal would hide the spotlight); role + aria-modal + the focus trap keep the semantics.
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={onCardKeyDown}
        data-testid="spotlight-tour-card"
        className={cn(
          "fixed z-[61] rounded-lg border border-border bg-bg-elevated p-4 text-fg shadow-lg",
          "animate-scale-in motion-reduce:animate-none",
        )}
        style={{ ...cardStyle, width: cardW, maxWidth: CARD_MAX_W }}
        key={stop.id}
      >
        <p className="text-xs text-fg-subtle" data-testid="spotlight-tour-count">
          Step {step} of {total}
        </p>
        <h2 id={titleId} className="mt-1 text-base font-semibold tracking-tight">
          {stop.title}
        </h2>
        <p id={bodyId} className="mt-1 text-sm text-fg-muted">
          {stop.body}
        </p>
        <div className="mt-4 flex items-center justify-between gap-2">
          {last ? (
            <span />
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="rounded-sm text-xs text-fg-subtle underline-offset-4 hover:text-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
              data-testid="spotlight-tour-skip"
            >
              Skip
            </button>
          )}
          <div className="flex items-center gap-2">
            {step > 1 ? (
              <Button variant="ghost" size="sm" onClick={onBack} data-testid="spotlight-tour-back">
                Back
              </Button>
            ) : null}
            <Button
              ref={primaryRef}
              size="sm"
              onClick={last ? onClose : onNext}
              data-testid={last ? "spotlight-tour-done" : "spotlight-tour-next"}
            >
              {last ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function sameBox(a: Box, b: Box): boolean {
  return a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** ~55% of the page's dark colour: the text colour on a light theme, the
 *  page colour on a dark one. */
function dimColour(): string {
  let dark = false;
  try {
    dark = getComputedStyle(document.documentElement).colorScheme.includes("dark");
  } catch {
    dark = false;
  }
  return dark ? "rgb(var(--color-bg) / 0.7)" : "rgb(var(--color-fg) / 0.55)";
}
